import { Injectable } from '@angular/core';
import { PaymentCollectionConstants, RideUtil } from '@uzurv/clara';
import { isNullOrUndefined, RideRequestTypeConstants, VehicleTypeConstants } from '@uzurv/core';
import moment from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { OrganizationRiderState } from '../../store/organization-rider/organization-rider.reducer';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

@Injectable()
export class NewItineraryValidationService {
  constructor(private featureFlagService: FeatureFlagService) {}

  validate(currentState: OrganizationRiderState, skipPaymentValidation: boolean = false): Observable<Array<string>> {
    const isCreditCardValidationEnabled = this.featureFlagService.isCreditCardValidationEnabled();

    const errors: Array<string> = [];

    if (isNullOrUndefined(currentState.rider)) {
      errors.push('Select a rider');
    }

    if (isNullOrUndefined(currentState.selectedVehicleType)) {
      errors.push('Select a service level');
    }

    if (currentState.isRideSubscription && isNullOrUndefined(currentState.selectedDaysOfTheWeek)) {
      errors.push('Select days of the week for the ride subscription');
    }

    if (currentState.isRideSubscription && isNullOrUndefined(currentState.subscriptionStartDate)) {
      errors.push('Select a start date for the ride subscription');
    }

    if (currentState.isRideSubscription && !isNullOrUndefined(currentState.subscriptionStartDate)) {
      if (moment(currentState.subscriptionStartDate).isBefore(new Date())) {
        errors.push(`Start date cannot be before today's date.`);
      }
    }

    if (currentState.isRideSubscription && currentState.subscriptionStartDate && isNullOrUndefined(currentState.subscriptionEndDate)) {
      errors.push('Select an end date for the ride subscription');
    }

    if (currentState.isRideSubscription && currentState.subscriptionStartDate && !isNullOrUndefined(currentState.subscriptionEndDate)) {
      if (moment(currentState.subscriptionEndDate).isBefore(currentState.subscriptionStartDate)) {
        errors.push('Subscription end date cannot be before subscription start date.');
      }
    }

    if (isNullOrUndefined(currentState.itineraryDate)) {
      errors.push('Select a date for the itinerary');
    }

    // TODO: Refactor this to loop through Ride Requests and their transit programs, show appropriate message on the leg UI

    if (!currentState.isWavEnabled && !isNullOrUndefined(currentState.selectedVehicleType)) {
      if (currentState.selectedVehicleType.code === VehicleTypeConstants.WAV) {
        errors.push(`The selected transit program does not support WAV vehicle types`);
      }
    }

    currentState.itineraryLegs.forEach((leg, index) => {
      const legNumber = index + 1;

      if (isNullOrUndefined(leg.rideRequestType)) {
        errors.push(`Select a ride request type for leg ${legNumber}`);
      }

      if (leg.rideRequestType && !leg.rideRequestType.onDemand) {
        if (isNullOrUndefined(leg.pickupDate)) {
          errors.push(`Leg ${legNumber} requires a pickup date`);
        }

        if (isNullOrUndefined(leg.dropoffDate)) {
          errors.push(`Leg ${legNumber} requires a dropoff date`);
        }
      }

      if (leg.rideRequestType && leg.rideRequestType.code === RideRequestTypeConstants.SCHEDULED && isNullOrUndefined(leg.dropoffDate)) {
        errors.push(`Leg ${legNumber} requires a dropoff date`);
      }

      if (isNullOrUndefined(leg.pickupLocation)) {
        errors.push(`Select a pickup location for leg ${legNumber}`);
      }

      if (isNullOrUndefined(leg.dropoffLocation)) {
        errors.push(`Select a dropoff location for leg ${legNumber}`);
      }

      if (leg.rideRequestType) {
        if (currentState.selectedVehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV) {
          if (leg.rideRequestType?.code === RideRequestTypeConstants.RIDE_NOW && !currentState.isOnDemandWavEnabled) {
            errors.push(`WAV Ride Now is not allowed for leg ${legNumber}`);
          }
          if (leg.rideRequestType?.code === RideRequestTypeConstants.WILL_CALL && !currentState.isWavWillCallEnabled) {
            errors.push(`WAV Will Call is not allowed for leg ${legNumber}`);
          }
        }

        if (
          !moment(currentState.itineraryDate).isSame(moment(), 'day') &&
          (leg.rideRequestType.code === RideRequestTypeConstants.RECOVERY || leg.rideRequestType.code === RideRequestTypeConstants.RIDE_NOW)
        ) {
          errors.push(`Leg ${legNumber} ride type is only avilable for today's date`);
        }
      }

      if (currentState.organization.clientInvoiceEstimateRequired && isNullOrUndefined(leg.clientInvoiceEstimate)) {
        errors.push(`Leg ${legNumber} requires an value for client invoice estimate`);
      }

      if (leg.rideMetadataValues && leg.rideMetadataValues.length !== 0) {
        leg.rideMetadataValues.forEach(value => {
          if (value.rideMetadataField && value.rideMetadataField.required && (isNullOrUndefined(value.value) || value.value.length === 0)) {
            errors.push(`Leg ${legNumber} requires an answer for ${value.rideMetadataField.name}`);
          }
        });
      }

      if (currentState.organization.clientInvoiceEstimateRequired && isNullOrUndefined(leg.clientInvoiceEstimate)) {
        errors.push(`Leg ${legNumber} requires an value for client invoice estimate`);
      }

      if (leg.rideMetadataValues && leg.rideMetadataValues.length !== 0) {
        leg.rideMetadataValues.forEach(value => {
          if (value.rideMetadataField && value.rideMetadataField.required && (isNullOrUndefined(value.value) || value.value.length === 0)) {
            errors.push(`Leg ${legNumber} requires an answer for ${value.rideMetadataField.name}`);
          }
        });
      }
    });

    if (!skipPaymentValidation) {
      if (
        currentState.organization.riderPaymentRequired &&
        currentState.organization.riderPaymentAccountEnabled &&
        !isNullOrUndefined(currentState.selectedOrganizationPaymentMethod) &&
        currentState.selectedOrganizationPaymentMethod.toString() === PaymentCollectionConstants.PAYMENT_ACCOUNT &&
        (isNullOrUndefined(currentState.selectedPaymentAccount) ||
          (isCreditCardValidationEnabled && !currentState.selectedPaymentAccount.verified))
      ) {
        errors.push('Select a payment account');
      }

      if (currentState.organization.riderPaymentRequired && isNullOrUndefined(currentState.selectedOrganizationPaymentMethod)) {
        errors.push('Select a payment method');
      }
    }

    return of(errors);
  }
}
